import React, { useContext } from 'react';

import UIContext from 'components/Utils/UIContext';
import { faClone, faClose, faEdit, faEyeSlash, faTrash } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ActionButton from "components/action/ActionButton";

import { useTranslation } from "react-i18next";

import "./StakeGeneralTab.sass";
import { Button, Flex, Tag, Tooltip } from 'antd';
import { keyBy, sortBy } from 'lodash';
import Weight from 'components/controls/Weight';
import { ImpactRelationSection } from 'components/tabs/ImpactRelationSection';
import { FishboneLinksReadonly } from 'components/tabs/FishboneLinksReadonly';
import { useIndicatorImpactController } from 'hooks/useIndicatorImpactController';

interface StakeGeneralTabProps {
    selection: any;
    onEdit?: Function;
    onClone?: Function;
    onClose: Function;
    onDisable?: Function;
    onDelete?: Function;
    scenario: any;

    risks: any[];
    indicators?: any[];

    stakes?: any[];
    fishboneBranchs?: any[];
    createStakeImpact: Function;
    deleteStakeImpact: Function;
    updateImpactStrength: Function;

    hideActions?: boolean;
}

export const StakeGeneralTab = (props: StakeGeneralTabProps) => {

    const { risks = [] } = props;
    const { t } = useTranslation();
    const uiContext = useContext(UIContext);

    const realRisks = risks.filter(r => !r.isOpportunity);
    const realOpportunities = risks.filter(r => r.isOpportunity);

    const { createStakeIndicatorImpact, updateStakeIndicatorImpactStrength, deleteStakeIndicatorImpact } = useIndicatorImpactController();

    const indexedRisks = keyBy(risks, 'id');

    return (
        <div className="StakeGeneralTab">

            <Flex vertical>

                <Flex className="mb-1">
                    <div className="w-100 text-left">
                        <div className="title">{props.selection?.name}</div>
                        <small>{t("models.stake.name")}</small>
                    </div>
                    <Tooltip title={t('actions.close')} placement='left'>

                        <Button
                            shape="circle"
                            variant="solid"
                            className="mini ml-auto"
                            tooltip={t('models.item.actions.edit')}
                            style={{ marginRight: '-0.9rem' }}
                            onClick={props.onClose}
                        >
                            <FontAwesomeIcon icon={faClose} />
                        </Button></Tooltip>
                </Flex>

                <ul>
                    {props.selection?.description && (
                        <li className="EditableListItem stake-description">
                            <div>{props.selection?.description}</div>
                        </li>
                    )}
                </ul>

                {uiContext.canEdit && !props.hideActions && <div className="Actions mt-1 mb-1 pl-1 text-center ml-auto mr-auto" data-testid="stake-actions">
                    {props.onEdit && <ActionButton
                        className="small ml-05"
                        tooltip={t('actions.edit')}
                        data-testid="action-edit"
                        onClick={() => props.onEdit(props.selection.id)}
                    >
                        <FontAwesomeIcon icon={faEdit} />
                    </ActionButton>}

                    {false && props.onClone && <ActionButton
                        className="small"
                        tooltip={t('actions.clone')}
                        data-testid="action-clone"
                        onClick={() => props.onClone(props.selection)}
                    >
                        <FontAwesomeIcon icon={faClone} />
                    </ActionButton>}

                    {props.onDisable && <ActionButton
                        className="small"
                        tooltip={t('actions.disable')}
                        data-testid="action-disable"
                        onClick={() => props.onDisable(props.selection.id)}
                    >
                        <FontAwesomeIcon icon={faEyeSlash} />
                    </ActionButton>}

                    {props.onDelete && <ActionButton
                        className="small"
                        tooltip={t('actions.delete')}
                        data-testid="action-delete"
                        onClick={() => props.onDelete(props.selection.id)}
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </ActionButton>}
                </div>}

                <FishboneLinksReadonly
                    title={t('pages.fishbone.title')}
                    links={props.selection?.fishboneLinks}
                    fishboneBranchs={props.fishboneBranchs}
                />

                {(
                    <Flex vertical className="mt-2">
                        <div className="text-left mb-05">
                            <strong>{t('models.stake.fields.risks.name')}</strong>
                        </div>
                        <div className="text-left">
                            {realRisks && realRisks.length ? (
                                sortBy(realRisks, 'name').map(
                                    (r: any, index) => {
                                        const impactedStake = r.impactedStakes.find(ii => ii.stakeId == props.selection?.id);
                                        return (
                                            <Tag.CheckableTag
                                                key={index}
                                                checked={impactedStake?.id}
                                                onClick={async (e) => {

                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    if (!uiContext.canEdit) return;
                                                    if (
                                                        !impactedStake ||
                                                        !impactedStake.id
                                                    ) {
                                                        props.createStakeImpact(
                                                            r.id,
                                                            props.selection?.id,

                                                        );
                                                    } else if (
                                                        impactedStake.strength <
                                                        3
                                                    ) {
                                                        props.updateImpactStrength(
                                                            impactedStake,
                                                            impactedStake.strength +
                                                            1
                                                        );
                                                    } else {
                                                        props.deleteStakeImpact(
                                                            impactedStake.id,
                                                            r.id
                                                        );
                                                    }
                                                }}
                                                className={[
                                                    'ImpactedStake tag noselection'
                                                ].join(' ')}
                                            >

                                                <span className="tag__label" title={r.name}>{r.name}</span>

                                                {impactedStake ? (
                                                    <Weight onClick={() => { }}>
                                                        {
                                                            impactedStake.strength
                                                        }
                                                    </Weight>
                                                ) : null}
                                            </Tag.CheckableTag>
                                        );
                                    }
                                )
                            ) : (
                                <div
                                    className="text-center"
                                    style={{ color: 'var(--ant-color-text-tertiary)' }}
                                >
                                    {t('models.risk.messages.no_x')}
                                </div>
                            )}
                        </div>
                    </Flex>
                )}

                {(
                    <Flex vertical className="mt-2">
                        <div className="text-left mb-05">
                            <strong>{t('models.stake.fields.risks.name_positive')}</strong>
                        </div>
                        <div className="text-left">
                            {realOpportunities && realOpportunities.length ? (
                                sortBy(realOpportunities, 'name').map(
                                    (r: any, index) => {
                                        const impactedStake = r.impactedStakes.find((ii: any) => ii.stakeId == props.selection?.id);

                                        return (
                                            <Tag.CheckableTag
                                                key={index}
                                                checked={impactedStake?.id}
                                                onClick={async (e) => {

                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    if (!uiContext.canEdit) return;
                                                    if (
                                                        !impactedStake ||
                                                        !impactedStake.id
                                                    ) {
                                                        props.createStakeImpact(
                                                            r.id,
                                                            props.selection?.id,

                                                        );
                                                    } else if (
                                                        impactedStake.strength <
                                                        3
                                                    ) {
                                                        props.updateImpactStrength(
                                                            impactedStake,
                                                            impactedStake.strength +
                                                            1
                                                        );
                                                    } else {
                                                        props.deleteStakeImpact(
                                                            impactedStake.id,
                                                            r.id
                                                        );
                                                    }
                                                }}
                                                className={[
                                                    'ImpactedStake tag noselection'
                                                ].join(' ')}
                                            >

                                                <span className="tag__label" title={r.name}>{r.name}</span>

                                                {impactedStake ? (
                                                    <Weight onClick={() => { }}>
                                                        {
                                                            impactedStake.strength
                                                        }
                                                    </Weight>
                                                ) : null}
                                            </Tag.CheckableTag>
                                        );
                                    }
                                )
                            ) : (
                                <div
                                    className="text-center"
                                    style={{ color: 'var(--ant-color-text-tertiary)' }}
                                >
                                    {t('models.risk.messages.no_x_positive')}
                                </div>
                            )}
                        </div>
                    </Flex>
                )}

                <ImpactRelationSection
                    title={t('models.item.fields.indicators.name')}
                    items={props.indicators || []}
                    scenario={props.scenario}
                    emptyMessage={t('models.indicator.no_x')}
                    getLink={(ind) =>
                        (props.selection?.impactedIndicators || []).find(
                            (x: any) => x.indicatorId === ind.id
                        )
                    }
                    onToggle={(ind, link) => {
                        if (!link || !link.id) {
                            createStakeIndicatorImpact(props.selection?.id, ind.id);
                        } else if (link.strength < 3) {
                            updateStakeIndicatorImpactStrength(link, link.strength + 1);
                        } else {
                            deleteStakeIndicatorImpact(link.id, props.selection?.id);
                        }
                    }}
                />
            </Flex>
        </div >
    );
}